import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Clock, Facebook, Loader2, CheckCircle } from 'lucide-react';
import { restaurantInfo, faqData } from '../data/mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_ORDER = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openingHours, setOpeningHours] = useState({});

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/settings/hours`)
      .then(res => setOpeningHours(res.data.hours || {}))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/send`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message
      });
      
      toast.success('Message envoyé !', {
        description: 'Nous vous répondrons dans les plus brefs délais.'
      });
      
      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      
      // Reset submitted state after 5 seconds
      setTimeout(() => setIsSubmitted(false), 5000);
      
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur', {
        description: 'Une erreur est survenue. Veuillez réessayer ou nous appeler directement.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-black">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className="text-5xl lg:text-6xl font-bold text-white mb-4"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            <span className="text-[#D4AF37]">Contact</span> & Localisation
          </h2>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            Venez nous rendre visite ou contactez-nous
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Contact Info */}
          <div className="space-y-8">
            {/* Address */}
            <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10">
              <div className="flex items-start space-x-4">
                <div className="bg-[#D4AF37] p-3 rounded-lg">
                  <MapPin size={24} className="text-black" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white text-lg mb-2">Adresse</h3>
                  <p className="text-white/80 mb-2">{restaurantInfo.address}</p>
                  <p className="text-sm text-white/60">Plus Code: {restaurantInfo.plusCode}</p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurantInfo.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 text-[#D4AF37] hover:text-[#C5A028] font-semibold transition-colors"
                  >
                    Obtenir l'itinéraire →
                  </a>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10">
              <div className="flex items-start space-x-4">
                <div className="bg-[#D4AF37] p-3 rounded-lg">
                  <Phone size={24} className="text-black" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white text-lg mb-2">Téléphone</h3>
                  <a
                    href={`tel:${restaurantInfo.phone1.replace(/\s/g, '')}`}
                    className="block text-white/80 hover:text-[#D4AF37] transition-colors mb-1"
                  >
                    {restaurantInfo.phone1}
                  </a>
                </div>
              </div>
            </div>

            {/* Hours */}
            <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10">
              <div className="flex items-start space-x-4">
                <div className="bg-[#D4AF37] p-3 rounded-lg">
                  <Clock size={24} className="text-black" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white text-lg mb-4">Horaires d'ouverture</h3>
                  <div className="space-y-2">
                    {DAYS_ORDER.map((day) => (
                      <div key={day} className="flex justify-between items-center">
                        <span className="text-white/80 capitalize font-medium">{day}</span>
                        <span className="text-white/60 text-sm">{openingHours[day] || '-'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10">
              <h3 className="font-bold text-white text-lg mb-4">Suivez-nous</h3>
              <div className="flex space-x-4">
                <a
                  href="https://www.snapchat.com/add/cosmos_officiel"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#FFFC00] p-3 rounded-lg hover:opacity-80 transition-opacity"
                  title="Snapchat"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="black">
                    <path d="M12.206 2c1.368 0 2.605.391 3.512.922.897.527 1.527 1.261 1.818 2.01.182.464.273 1.108.273 1.915v.162c0 .398-.015.746-.044 1.047l.004.045c1.281.094 2.064.592 2.348 1.495.062.196.088.4.076.604-.012.204-.062.407-.15.6-.343.75-1.097 1.142-2.24 1.162-.063.002-.127.003-.19.003-.22 0-.417-.018-.598-.052-.06-.012-.119-.026-.177-.042-.08.16-.155.32-.225.48-.317.726-.539 1.345-.67 1.855-.028.11-.05.211-.066.303-.012.068-.023.131-.032.19-.012.082-.021.154-.026.217-.006.063-.009.119-.009.168 0 .264.054.477.162.637.108.16.299.3.574.42.275.12.668.23 1.178.33.115.023.21.09.27.186.06.096.082.21.062.32-.04.222-.121.427-.243.615-.242.376-.61.68-1.099.912-.978.464-2.377.697-4.198.697-1.82 0-3.22-.233-4.198-.697-.49-.232-.857-.536-1.1-.912-.121-.188-.202-.393-.242-.615-.02-.11.002-.224.062-.32.06-.096.155-.163.27-.186.51-.1.903-.21 1.178-.33.275-.12.466-.26.574-.42.108-.16.162-.373.162-.637 0-.049-.003-.105-.009-.168-.005-.063-.014-.135-.026-.217-.009-.059-.02-.122-.032-.19-.016-.092-.038-.193-.066-.303-.131-.51-.353-1.129-.67-1.855-.07-.16-.145-.32-.225-.48-.058.016-.117.03-.177.042-.181.034-.378.052-.598.052-.063 0-.127-.001-.19-.003-1.143-.02-1.897-.412-2.24-1.162-.088-.193-.138-.396-.15-.6-.012-.204.014-.408.076-.604.284-.903 1.067-1.401 2.348-1.495l.004-.045c-.029-.301-.044-.649-.044-1.047v-.162c0-.807.091-1.451.273-1.915.291-.749.921-1.483 1.818-2.01C9.601 2.391 10.838 2 12.206 2z"/>
                  </svg>
                </a>
                <a
                  href="https://www.facebook.com/Le-Cosmos-Saint-Etienne"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#1877F2] p-3 rounded-lg hover:opacity-80 transition-opacity"
                  title="Facebook"
                >
                  <Facebook size={24} className="text-white" />
                </a>
                <a
                  href="https://www.instagram.com/le_cosmos42_/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] p-3 rounded-lg hover:opacity-80 transition-opacity"
                  title="Instagram"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a
                  href="https://www.tiktok.com/@lecosmos42"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black p-3 rounded-lg hover:opacity-80 transition-opacity border border-white/30"
                  title="TikTok"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white/5 backdrop-blur-sm p-8 rounded-xl border border-white/10">
            <h3 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
              Envoyez-nous un message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-white/80 mb-2 font-medium">Nom</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#D4AF37] focus:bg-white/15 transition-all"
                  placeholder="Votre nom"
                />
              </div>
              <div>
                <label className="block text-white/80 mb-2 font-medium">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#D4AF37] focus:bg-white/15 transition-all"
                  placeholder="votre.email@exemple.com"
                />
              </div>
              <div>
                <label className="block text-white/80 mb-2 font-medium">Téléphone (optionnel)</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#D4AF37] focus:bg-white/15 transition-all"
                  placeholder="06 12 34 56 78"
                />
              </div>
              <div>
                <label className="block text-white/80 mb-2 font-medium">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#D4AF37] focus:bg-white/15 transition-all resize-none"
                  placeholder="Votre message..."
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={isSubmitting || isSubmitted}
                className={`w-full px-8 py-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 ${
                  isSubmitted 
                    ? 'bg-green-500 text-white cursor-default'
                    : isSubmitting
                    ? 'bg-[#D4AF37]/70 text-black cursor-wait'
                    : 'bg-[#D4AF37] text-black hover:bg-[#C5A028] hover:transform hover:-translate-y-1 hover:shadow-xl'
                }`}
              >
                {isSubmitted ? (
                  <>
                    <CheckCircle size={20} />
                    <span>Message envoyé !</span>
                  </>
                ) : isSubmitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    <span>Envoi en cours...</span>
                  </>
                ) : (
                  <span>Envoyer le message</span>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Map */}
        <div className="mb-16">
          <div className="bg-white/5 backdrop-blur-sm p-2 rounded-xl border border-white/10 overflow-hidden">
            <iframe
              src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2784.8!2d4.393!3d45.438!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDXCsDI2JzE2LjgiTiA0wrAyMyczNC44IkU!5e0!3m2!1sfr!2sfr!4v1234567890`}
              width="100%"
              height="400"
              style={{ border: 0, borderRadius: '8px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Le Cosmos Saint-Étienne Location"
            ></iframe>
          </div>
        </div>

        {/* FAQ Section */}
        <div>
          <h3
            className="text-4xl font-bold text-white text-center mb-12"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            Questions <span className="text-[#D4AF37]">fréquentes</span>
          </h3>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-white hover:text-[#D4AF37] hover:no-underline py-4">
                    <span className="font-semibold text-left">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-white/70 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
